
import React, { useMemo, useState } from 'react'
import {
  Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button,  Typography, Backdrop, CircularProgress, Card, CardContent, Radio, RadioGroup, FormControlLabel, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import PaymentForm from '../CreateAccount/PaymentForm'
import { gql } from '@apollo/client';
import { useMutation, useQuery } from "@apollo/client/react";
import { DEALSAUCE_SUBSCRIBE } from './mutations'
import { GET_ME } from './queries'

const DEALSAUCE_PRICES = gql`
  query dealsaucePrices($priceIds: [String!]!) {
    dealsaucePrices(priceIds: $priceIds) {
      id
      amount
      interval
      active
    }
  }
`

const DEALSAUCE_PAYMENT_METHODS = gql`
  query dealsaucePaymentMethods($orgId: ID) {
    dealsaucePaymentMethods(orgId: $orgId) {
      id
      brand
      last4
      expMonth
      expYear
      isDefault
    }
  }
`

const DEALSAUCE_SET_DEFAULT_PM = gql`
  mutation dealsauceSetDefaultPaymentMethod(
    $orgId: ID
    $paymentMethodId: String!
  ) {
    dealsauceSetDefaultPaymentMethod(
      orgId: $orgId
      paymentMethodId: $paymentMethodId
    )
  }
`

const priceIds = [
  'plan_RFRNZc45vs8Anz',
  'price_1ROjLGG0Nqc9OHilgy9wsVLL',
]

const stripePublicKey =
  import.meta.env.VITE_DEALSAUCE_STRIPE_PUBLIC_KEY ||
  import.meta.env.VITE_STRIPE_PUBLIC_KEY
const stripePromise = loadStripe(stripePublicKey)

const currencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0 })

const AffiliateSubscribeDialog = ({ open, onClose, session  }) => {
  const [yearly, setYearly] = useState(false)
  const [stripeToken, setStripeToken] = useState('')
  const [element, setElement] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [useExisting, setUseExisting] = useState(false)
  const [errorText, setErrorText] = useState('')
  const [selectedPm, setSelectedPm] = useState('')

  const orgId =
    session?.me?.organization ||
    session?.me?.activeOrg?._id ||
    session?.me?.activeOrgId ||
    null

  const { data: pmData, refetch: refetchPms } = useQuery(
    DEALSAUCE_PAYMENT_METHODS,
    {
      // Backend can infer orgId from auth; send only if known
      variables: orgId ? { orgId } : {},
      skip: !open,
      fetchPolicy: 'network-only'},
  )
  const paymentMethods = pmData?.dealsaucePaymentMethods || []

  const { data } = useQuery(DEALSAUCE_PRICES, {
    variables: { priceIds },
    skip: !open,
    fetchPolicy: 'cache-first'})
  const byId = useMemo(() => {
    const map = {}
    ;(data?.dealsaucePrices || []).forEach((p) => (map[p.id] = p))
    return map
  }, [data])
  const monthly = byId['plan_RFRNZc45vs8Anz']?.amount || null
  const annual =
    byId['price_1ROjLGG0Nqc9OHilgy9wsVLL']?.amount || null
  const display = yearly
    ? currencyFormat.format(Math.round((annual || 0) / 1200))
    : currencyFormat.format(Math.round((monthly || 0) / 100))

  const priceId = yearly
    ? 'price_1ROjLGG0Nqc9OHilgy9wsVLL'
    : 'plan_RFRNZc45vs8Anz'
  const planTypeValue = yearly ? 'annual' : 'monthly'

  const hasExistingCard = (paymentMethods || []).length > 0

  const [dealsauceSubscribe] = useMutation(DEALSAUCE_SUBSCRIBE, {
    refetchQueries: [{ query: GET_ME }],
  })
  const [setDefaultPm] = useMutation(DEALSAUCE_SET_DEFAULT_PM)

  const createToken = async (stripe, cardElement, nameOnCard) => {
    if (!nameOnCard) setStripeToken('')
    else {
      const res = await stripe.createToken(cardElement, {
        name: nameOnCard,
      })
      if (res.error) setStripeToken('')
      else setStripeToken(res.token)
    }
  }

  // When toggling to Existing Card, auto-select default or first
  React.useEffect(() => {
    if (useExisting && hasExistingCard && !selectedPm) {
      const def = paymentMethods.find((p) => p.isDefault)
      setSelectedPm(def ? def.id : paymentMethods[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useExisting, hasExistingCard, paymentMethods])

  const handleExistingToggle = () => {
    const next = !useExisting
    setUseExisting(next)
    setErrorText('')
    if (next) {
      refetchPms()
      if (hasExistingCard) {
        const def = paymentMethods.find((p) => p.isDefault)
        setSelectedPm(def ? def.id : paymentMethods[0].id)
      }
    } else {
      setSelectedPm('')
    }
  }

  const handlePickPm = async (pmId) => {
    setSelectedPm(pmId)
    try {
      await setDefaultPm({
        variables: { orgId, paymentMethodId: pmId }})
      await refetchPms()
    } catch (e) {}
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setErrorText('')
    try {
      await dealsauceSubscribe({
        variables: {
          orgId: session?.me?.organization,
          email: session?.me?.email,
          priceId,
          stripeToken: useExisting ? null : stripeToken,
          coupon: null,
          useExistingCard: useExisting,
          paymentMethodId: useExisting ? selectedPm || null : null,
          planType: planTypeValue}})
      setSubmitting(false)
      onClose && onClose()
    } catch (e) {
      setSubmitting(false)
      const msg =
        (e?.graphQLErrors && e.graphQLErrors[0]?.message) ||
        e?.message ||
        'Subscription failed.'
      // Strip common wrappers
      const clean = String(msg)
        .replace(/^GraphQL error:\s*/i, '')
        .replace(/^Error:\s*/i, '')
      setErrorText(clean)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      {submitting && (
        <Backdrop open={true} style={{ color: '#fff', zIndex: 2000 }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <DialogTitle>Upgrade Your Plan</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid style={{ width: '100%', marginTop: 10 }}>
            <Grid
              container
              direction="column"
              style={{ marginBottom: 10 }}
              sx={{
                alignItems: 'center',
              }}
            >
              <ToggleButtonGroup
                exclusive
                size="small"
                value={yearly ? 'annual' : 'monthly'}
                onChange={(_event, next) => {
                  if (next == null) return
                  setYearly(next === 'annual')
                }}
                aria-label="Billing interval"
                sx={{
                  borderRadius: "0.65rem",
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    fontWeight: 700,
                    px: 2,
                    py: 0.75,
                    color: "#4f5062",
                    borderColor: "#e6e7f1",
                    bgcolor: "#fff",
                  },
                  "& .MuiToggleButton-root.Mui-selected": {
                    bgcolor: "#368cff",
                    color: "#fff",
                    borderColor: "#368cff",
                    "&:hover": { bgcolor: "#1a6fd4" },
                  },
                }}
              >
                <ToggleButton value="monthly">Pay Monthly</ToggleButton>
                <ToggleButton value="annual">Pay Annually</ToggleButton>
              </ToggleButtonGroup>
              <Typography
                sx={{
                  mt: 0.5,
                  color: '#2096f3',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                }}
              >
                Save 20%
              </Typography>
            </Grid>
          </Grid>
          <Grid style={{ width: '100%' }}>
            <Grid container direction="column" spacing={1}>
              <Grid>
                <Typography
                  variant="h4"
                  align="center"
                  style={{ fontWeight: 700 }}
                >
                  {`Only ${display}/mo`}
                </Typography>
              </Grid>
              <Grid>
                <Typography
                  align="center"
                  variant="subtitle1"
                  style={{ color: '#2096f3' }}
                >
                  {yearly
                    ? '*when purchased annually · Billed annually'
                    : '*when purchased monthly'}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Payment summary */}
          <Grid style={{ width: '100%' }}>
            <Card style={{ boxShadow: 'none', marginBottom: -10 }}>
              <CardContent
                style={{
                  backgroundColor: '#e3e3e3',
                  paddingLeft: 20,
                  paddingRight: 20,
                  paddingTop: 10,
                  paddingBottom: 10}}
              >
                <Grid
                  container
                  direction="column"
                  sx={{
                    justifyContent: "space-between"
                  }}
                >
                  <Grid>
                    <Typography>
                      {yearly ? 'Annual ' : 'Monthly '}Investor Plan
                    </Typography>
                  </Grid>
                  <Grid container sx={{
                    justifyContent: "space-between"
                  }}>
                    <Grid>
                      <Typography>Subtotal</Typography>
                    </Grid>
                    <hr
                      style={{
                        width: '60%',
                        borderWidth: 0,
                        backgroundColor: 'black',
                        height: '1px',
                        marginTop: 'auto',
                        marginBottom: 'auto'}}
                    />
                    <Grid>
                      <Typography>
                        {yearly && annual
                          ? currencyFormat.format(
                              Math.round(annual / 100),
                            )
                          : monthly
                            ? currencyFormat.format(
                                Math.round(monthly / 100),
                              )
                            : '$0'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container sx={{
                  justifyContent: "space-between"
                }}>
                  <Grid>
                    <Typography>Total due today</Typography>
                  </Grid>
                  <hr
                    style={{
                      width: '45%',
                      borderWidth: 0,
                      backgroundColor: 'black',
                      height: '1px',
                      marginTop: 'auto',
                      marginBottom: 'auto'}}
                  />
                  <Grid>
                    <Typography>
                      {yearly && annual
                        ? currencyFormat.format(
                            Math.round(annual / 100),
                          )
                        : monthly
                          ? currencyFormat.format(
                              Math.round(monthly / 100),
                            )
                          : '$0'}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid>
                  <Typography style={{ fontWeight: 900 }}>
                    {`Next payment due a ${yearly ? 'year' : 'month'} from now.`}
                  </Typography>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Existing card list or message */}
          <Grid style={{ width: '100%' }}>
            {hasExistingCard ? (
              <>
                <Grid
                  container
                  direction="row"
                  style={{ marginBottom: 10 }}
                  sx={{
                    justifyContent: "space-evenly"
                  }}
                >
                  <Grid>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={useExisting ? 'existing' : 'new'}
                      onChange={(_event, next) => {
                        if (next == null) return
                        if ((next === 'existing') !== useExisting) {
                          handleExistingToggle()
                        }
                      }}
                      aria-label="Card source"
                      sx={{
                        borderRadius: "0.65rem",
                        "& .MuiToggleButton-root": {
                          textTransform: "none",
                          fontWeight: 700,
                          px: 2,
                          py: 0.75,
                          color: "#4f5062",
                          borderColor: "#e6e7f1",
                          bgcolor: "#fff",
                        },
                        "& .MuiToggleButton-root.Mui-selected": {
                          bgcolor: "#368cff",
                          color: "#fff",
                          borderColor: "#368cff",
                          "&:hover": { bgcolor: "#1a6fd4" },
                        },
                      }}
                    >
                      <ToggleButton value="new">New Card</ToggleButton>
                      <ToggleButton value="existing">Existing Card</ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                </Grid>
                {useExisting && (
                  <RadioGroup
                    value={selectedPm}
                    onChange={(e) => handlePickPm(e.target.value)}
                  >
                    {(paymentMethods || []).map((pm) => (
                      <FormControlLabel
                        key={pm.id}
                        value={pm.id}
                        control={<Radio />}
                        label={`${pm.brand || 'Card'} •••• ${pm.last4 || ''}  exp ${pm.expMonth || ''}/${pm.expYear || ''} ${pm.isDefault ? '(default)' : ''}`}
                      />
                    ))}
                  </RadioGroup>
                )}
              </>
            ) : (
              <Typography style={{ marginLeft: 12 }}>
                No existing cards on file. Please add a new card
                below.
              </Typography>
            )}
          </Grid>

          {!useExisting && (
            <Grid style={{ width: '100%' }}>
              <Elements stripe={stripePromise}>
                <PaymentForm
                  createToken={createToken}
                  setElement={setElement}
                  setResetToken={() => {}}
                />
              </Elements>
              <Typography>
                <div
                  id="card-errors"
                  role="alert"
                  style={{
                    color: 'red',
                    fontSize: 13,
                    marginTop: 3,
                    marginLeft: 12,
                    fontWeight: 400}}
                ></div>
              </Typography>
            </Grid>
          )}

          {Boolean(errorText) && (
            <Grid style={{ width: '100%' }}>
              <Typography
                role="alert"
                style={{ color: 'red', fontSize: 13, marginLeft: 12 }}
              >
                {errorText}
              </Typography>
            </Grid>
          )}

          <Grid>
            <Typography
              style={{
                marginLeft: 12,
                marginBottom: 5,
                fontSize: 13}}
            >
              By continuing, you agree to our{' '}
              <a href="/Terms" target="_blank" rel="noreferrer">
                Terms & Conditions
              </a>{' '}
              and our{' '}
              <a href="/Privacy" target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>CANCEL</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={
            (!useExisting && !stripeToken) ||
            (useExisting && hasExistingCard && !selectedPm)
          }
        >
          SUBMIT
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AffiliateSubscribeDialog
